import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable, catchError, map, of, shareReplay, tap } from 'rxjs';

import { getAemEndpointUrl, getAemRequestOptions } from '@hdms/shared/data-access';
import {
  AemNavigationResponse,
  EditorialResponseLink,
  FooterLinksResponse,
  GlobalBanner
} from '@hdms/shared/page-frame/types';
import { EnvironmentService, Logger } from '@hdms/shared/util-core';

export const AEM_NAVIGATION_ENDPOINT = 'allNavigation.json';

@Injectable({ providedIn: 'root' })
export class AemNavigationService {
  private readonly httpClient = inject(HttpClient);
  private readonly envService = inject(EnvironmentService);

  private readonly allNavigationData$ = this.getAllNavigationData$();

  public get editorialLinks$(): Observable<EditorialResponseLink[]> {
    return this.allNavigationData$.pipe(map(data => data.editorialNavigation || []));
  }

  public get footerLinks$(): Observable<FooterLinksResponse> {
    return this.allNavigationData$.pipe(map(data => data.footerNavigation));
  }

  public get globalBanner$(): Observable<GlobalBanner> {
    return this.allNavigationData$.pipe(map(({ globalBanner }) => globalBanner?.text && globalBanner));
  }

  private getAllNavigationData$(): Observable<AemNavigationResponse> {
    return this.httpClient
      .get<AemNavigationResponse>(
        getAemEndpointUrl(AEM_NAVIGATION_ENDPOINT, this.envService),
        getAemRequestOptions(this.envService)
      )
      .pipe(
        tap(data => {
          if (!data) {
            throw Error('no data returned');
          }
        }),
        catchError((error: HttpErrorResponse) => {
          Logger.server.error('Error retrieving AEM navigation data:', error.message);
          return of<AemNavigationResponse>({
            editorialNavigation: [],
            footerNavigation: null,
            globalBanner: null
          });
        }),
        shareReplay(1)
      );
  }
}
